import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MdSearch } from "react-icons/md";

const GROUP_LABELS = {
  ops: "العمليات",
  catalog: "الكتالوج",
  content: "المحتوى والتسويق",
  billing: "الفوترة",
  governance: "الإدارة",
  analytics: "التحليلات",
};

const QuickJump = ({ routes = [] }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 0);
      setQuery("");
      setActiveIdx(0);
    }
  }, [open]);

  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = routes
      .filter((r) => r.layout === "/admin" && r.path && r.name)
      .map((r) => ({
        name: r.name,
        path: `${r.layout}/${r.path}`,
        group: GROUP_LABELS[r.group] || r.group,
      }));
    if (!q) return list.slice(0, 12);
    return list
      .map((r) => ({ ...r, score: r.name.toLowerCase().includes(q) ? 1 : 0 }))
      .filter((r) => r.score > 0)
      .slice(0, 20);
  }, [query, routes]);

  if (!open) return null;

  const handleSelect = (item) => {
    setOpen(false);
    navigate(item.path);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-navy-900/40 p-6"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-xl rounded-2xl bg-white shadow-elevated dark:bg-navy-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-border-muted px-4 py-3 dark:border-navy-700">
          <MdSearch className="h-5 w-5 text-gray-500" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIdx(0);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActiveIdx((i) => Math.min(i + 1, items.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActiveIdx((i) => Math.max(i - 1, 0));
              } else if (e.key === "Enter") {
                if (items[activeIdx]) handleSelect(items[activeIdx]);
              }
            }}
            placeholder="بحث عن صفحة..."
            className="flex-1 bg-transparent text-sm text-navy-700 outline-none placeholder:text-gray-400 dark:text-white"
          />
          <kbd className="rounded border border-border bg-surface-muted px-2 py-0.5 text-xs text-gray-500">
            ESC
          </kbd>
        </div>
        <ul className="max-h-80 overflow-y-auto p-2">
          {items.length === 0 ? (
            <li className="px-3 py-6 text-center text-sm text-gray-500">
              لا توجد نتائج
            </li>
          ) : (
            items.map((item, idx) => (
              <li key={item.path}>
                <button
                  type="button"
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setActiveIdx(idx)}
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-right text-sm transition ${
                    idx === activeIdx
                      ? "bg-info-50 text-info-700"
                      : "text-navy-700 hover:bg-surface-muted dark:text-white dark:hover:bg-navy-700"
                  }`}
                >
                  <span className="font-medium">{item.name}</span>
                  {item.group ? (
                    <span className="text-xs text-gray-500">{item.group}</span>
                  ) : null}
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
};

export default QuickJump;
